/**
 * Shared entity-by-id IDB cache.
 *
 *   river__entity-by-id   (single DB, single store keyed by `_id`)
 *
 * Used by `useEntityById()` to satisfy one-off entity lookups (an automation's
 * icon, a task's parent project) without hitting REST every time. Bounded LRU
 * — oldest entries are evicted when the cache exceeds MAX_CACHED_ENTITIES.
 *
 * Storage pressure / age eviction (storageMonitor.ts) drops the entire DB when
 * it gets too stale; the cache is a pure acceleration layer with no durability
 * requirement.
 */

import { buildIDBKey } from './idbKeyManager';
import { idbManifest } from './idbManifest';
import { createFileLogger } from '../../utils/logger';
import type { BaseEntityType } from '../../types/entities';

const log = createFileLogger('ENTITY_BY_ID_IDB');

const DB_NAME = buildIDBKey('entity-by-id', '');
const DB_VERSION = 1;
const STORE = 'entities';
const META_STORE = 'meta';
const MAX_CACHED_ENTITIES = 5_000;

interface EntityByIdRecord {
  _id: string;
  entity: BaseEntityType;
  fetchedAt: number;
  lastAccessedAt: number;
}

let _registered = false;

function registerOnce(): void {
  if (_registered) return;
  _registered = true;
  idbManifest.register(DB_NAME, 'entity-by-id', '').catch(() => {});
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: '_id' });
        store.createIndex('by_last_accessed', 'lastAccessedAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'k' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function awaitReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getEntityById(id: string): Promise<BaseEntityType | null> {
  if (!id) return null;
  registerOnce();
  let db: IDBDatabase | null = null;
  try {
    db = await openDB();
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const record = (await awaitReq(store.get(id))) as EntityByIdRecord | undefined;
    if (record) {
      record.lastAccessedAt = Date.now();
      store.put(record);
    }
    await awaitTx(tx).catch(() => {});
    idbManifest.touchLastAccessed(DB_NAME).catch(() => {});
    return record?.entity ?? null;
  } catch (err) {
    log.warn(`[EntityByIdIDB] getEntityById(${id}) failed`, err);
    return null;
  } finally {
    db?.close();
  }
}

export async function putEntityById(entity: BaseEntityType): Promise<void> {
  if (!entity?._id) return;
  registerOnce();
  let db: IDBDatabase | null = null;
  try {
    db = await openDB();
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const now = Date.now();
    store.put({
      _id: entity._id,
      entity,
      fetchedAt: now,
      lastAccessedAt: now,
    } as EntityByIdRecord);

    const count = await awaitReq(store.count());
    if (count > MAX_CACHED_ENTITIES) {
      const overflow = count - MAX_CACHED_ENTITIES;
      const idx = store.index('by_last_accessed');
      const cursorReq = idx.openCursor();
      let removed = 0;
      await new Promise<void>((resolve, reject) => {
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor || removed >= overflow) {
            resolve();
            return;
          }
          cursor.delete();
          removed += 1;
          cursor.continue();
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      });
    }

    await awaitTx(tx);
    idbManifest.touchLastAccessed(DB_NAME).catch(() => {});
  } catch (err) {
    log.warn(`[EntityByIdIDB] putEntityById(${entity._id}) failed`, err);
  } finally {
    db?.close();
  }
}

export async function deleteEntityById(id: string): Promise<void> {
  if (!id) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDB();
    const tx = db.transaction([STORE], 'readwrite');
    tx.objectStore(STORE).delete(id);
    await awaitTx(tx);
  } catch (err) {
    log.warn(`[EntityByIdIDB] deleteEntityById(${id}) failed`, err);
  } finally {
    db?.close();
  }
}
